import { defineStore } from "pinia";
import api from "@/modules/api";
import { useMate } from "@/stores/mate";
import { useMoment } from "@/stores/moment";
import { useScopedMateId } from "@/composable/useScopedMateId";
import { Follower } from '@/types/entity';

const PAGE_SIZE = 999;

interface FollowerState {
  following: {
    list: Follower[];
    changed: boolean;
  };
  follower: {
    list: Follower[];
    changed: boolean;
  };
}

export const useFollow = defineStore("follow", {
  state: (): FollowerState => {
    return {
      following: {
        list: [],
        changed: false,
      },
      follower: {
        list: [],
        changed: false,
      },
    };
  },
  getters: {
    followingMap: (state) => {
      const map = new Map<number, number>();
      state.following.list.forEach((item) => {
        map.set(item.mateId, item.id!);
      });
      return map;
    },
  },
  actions: {
    async getFollowingList(): Promise<Follower[]> {
      if (
        this.following.list &&
        !this.following.changed &&
        this.following.list.length > 0
      ) {
        return this.following.list;
      } else {
        return await this.fetchFollowingList();
      }
    },
    async getFollowerList(): Promise<Follower[]> {
      if (
        this.follower.list &&
        !this.follower.changed &&
        this.follower.list.length > 0
      ) {
        return this.follower.list;
      } else {
        return await this.fetchFollowerList();
      }
    },
    async fetchFollowingList(): Promise<Follower[]> {
      const mate = useMate();
      const res = await api.readFollowerPage({
        filter: { fanId: mate.userMate.id },
        pagination: { page: 1, perPage: PAGE_SIZE },
        sort: { order: "desc", field: "id" },
      });
      this.following.list = res.page.data;
      this.following.changed = false;
      return this.following.list;
    },
    async fetchFollowerList(): Promise<Follower[]> {
      const mate = useMate();
      const res = await api.readFollowerPage({
        filter: { mateId: mate.userMate.id },
        pagination: { page: 1, perPage: PAGE_SIZE },
        sort: { order: "desc", field: "id" },
      });
      this.follower.list = res.page.data;
      this.follower.changed = false;
      return this.follower.list;
    },
    async follow(mateId: number): Promise<Follower> {
      const scopedFollow = useScopedMateId(async () => {
        const res = await api.createOneFollower(mateId);
        this.following.changed = true;
        const moment = useMoment();
        moment.initMomentListIfEmpty(true, 1);
        return res.item;
      });
      return await scopedFollow();
    },
    /**
     * 取消关注
     * @param recordId 要取消关注的Follower的PK
     */
    async unfollow(recordId: number): Promise<void> {
      const scopedUnfollow = useScopedMateId(async () => {
        await api.deleteOneFollower(recordId);
        this.following.changed = true;
      });
      return await scopedUnfollow();
    },
    /**
     * 判断当前Mate是否关注了某个Mate
     * @param mateId 要判断的Mate的ID
     * @returns 如果关注了，返回Follower的PK；如果没有关注，返回null
     */
    async isFollowing(mateId: number): Promise<number | null> {
      const scopedIsFollowing = useScopedMateId(async () => {
        const mate = useMate();
        const res = await api.readFollowerPage({
          filter: { fanId: mate.userMate.id, mateId },
        });
        return res.page.data.length > 0 ? res.page.data[0].id! : null;
      });
      return await scopedIsFollowing();
    },
    disableCache(clearData = false): void {
      this.following.changed = true;
      this.follower.changed = true;
      if (clearData) {
        this.following.list = [];
        this.follower.list = [];
      }
    },
  },
});
